import { useState } from "react";
import { requestConsultation } from "../api";
import { pushEvent } from "../gtm";
import type { QuoteResult } from "../types";

interface Props {
  partner: string;
  quoteId: string;
  result: QuoteResult;
}

function formatPLN(value: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    maximumFractionDigits: 0,
  }).format(value);
}

export default function Step3Result({ partner, quoteId, result }: Props) {
  const addressLabel = `${result.street} ${result.house_number}, ${result.city_label}`;

  const [consultationLoading, setConsultationLoading] = useState(false);
  const [consultationSaved, setConsultationSaved] = useState(result.consultation_requested);
  const [consultationError, setConsultationError] = useState<string | null>(null);

  async function submitConsultation() {
    // Marketer wants the click itself, not the API result.
    pushEvent("detailed_valuation_click", { partner });
    setConsultationError(null);
    setConsultationLoading(true);
    try {
      await requestConsultation(partner, quoteId);
      setConsultationSaved(true);
    } catch (err) {
      setConsultationError(err instanceof Error ? err.message : "Nie udało się wysłać prośby");
    } finally {
      setConsultationLoading(false);
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h2 className="text-xl font-bold text-brand">Twoja orientacyjna wycena</h2>
        <p className="text-sm text-gray-600 mt-1">
          {result.area_m2} m², {addressLabel}
        </p>
      </header>

      <div className="bg-brand text-white rounded-2xl p-8 text-center">
        <div className="text-sm opacity-80 mb-2">Szacowany przedział wartości</div>
        <div className="text-3xl md:text-4xl font-bold">
          {result.method === "fallback_srednia_pl"
            ? `${formatPLN(result.price_min)} – ${formatPLN(result.price_max)}`
            : `${formatPLN(result.price_median)} – ${formatPLN(result.price_max)}`}
        </div>
      </div>

      <div className="bg-brand-50 border-2 border-brand-100 rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-bold text-brand">Co dalej?</h3>
        <p className="text-sm text-gray-800 leading-relaxed">
          Wycena mieszkania to nie tylko metraż i lokalizacja. Na cenę wpływają dziesiątki
          czynników — od stanu prawnego, przez standard wykończenia, aż po szczegóły konkretnego
          budynku.
        </p>
        <p className="text-sm text-gray-800 leading-relaxed">
          Nasz specjalista dokładniej przeanalizuje Twoją nieruchomość i skontaktuje się z Tobą w
          najbliższym czasie.
        </p>

        {consultationSaved ? (
          <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded text-sm text-gray-800">
            Dziękujemy — specjalista skontaktuje się z Tobą w najbliższym czasie.
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={submitConsultation}
              disabled={consultationLoading}
              className="w-full py-3 bg-brand text-white rounded-lg font-semibold hover:bg-brand-600 disabled:opacity-50 transition"
            >
              {consultationLoading ? "Wysyłam…" : "Poproś o dokładną wycenę"}
            </button>
            {consultationError && (
              <p className="text-xs text-red-600">{consultationError}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
